import { z } from "zod";
import { RiskPreferenceV1Schema } from "../ai-policy";
import { CalendarDateSchema } from "../campaign-creation";
import { SmartCreationObjectiveSchema, SmartCreationPromotionDateRangeSchema } from "./draft";

export const SmartCreationRecommendationApplicabilitySchema = z.enum([
  "current",
  "superseded",
  "expired",
]);
export type SmartCreationRecommendationApplicability = z.infer<
  typeof SmartCreationRecommendationApplicabilitySchema
>;

export const SmartCreationRecommendationFallbackReasonSchema = z.enum([
  "report_stale",
  "report_coverage_incomplete",
  "sample_too_small",
  "zero_attributed_sales",
  "missing_parent_history",
]);
export type SmartCreationRecommendationFallbackReason = z.infer<
  typeof SmartCreationRecommendationFallbackReasonSchema
>;

export const SmartCreationRecommendationEvidenceSchema = z
  .object({
    status: z.enum(["sufficient", "insufficient", "stale"]),
    reason: SmartCreationRecommendationFallbackReasonSchema.nullable(),
    primaryWindowDays: z.union([z.literal(14), z.literal(30)]),
    actualWindowDays: z.union([z.literal(14), z.literal(30), z.literal(60)]),
    expanded: z.boolean(),
    windowStart: CalendarDateSchema,
    windowEnd: CalendarDateSchema,
    dataThrough: CalendarDateSchema.nullable(),
    salesAttributionDays: z.literal(7),
    coverage: z
      .object({
        coveredDays: z.number().int().nonnegative(),
        expectedDays: z.number().int().positive(),
        complete: z.boolean(),
      })
      .strict(),
    sampleSize: z
      .object({
        selectedProducts: z.number().int().positive(),
        selectedParents: z.number().int().positive(),
        matchedProducts: z.number().int().nonnegative(),
        matchedParents: z.number().int().nonnegative(),
        productAds: z.number().int().nonnegative(),
        ambiguousAsins: z.number().int().nonnegative(),
        impressions: z.number().int().nonnegative(),
        clicks: z.number().int().nonnegative(),
        orders: z.number().int().nonnegative(),
      })
      .strict(),
    metrics: z
      .object({
        spend: z.number().finite().nonnegative(),
        sales: z.number().finite().nonnegative(),
        observedAcos: z.number().finite().nonnegative().nullable(),
        averageDailySpend: z.number().finite().nonnegative(),
      })
      .strict(),
    historicalReference: z
      .object({
        series: z.array(
          z
            .object({
              date: CalendarDateSchema,
              selectedProducts: z
                .object({
                  spend: z.number().finite().nonnegative(),
                  sales: z.number().finite().nonnegative(),
                  orders: z.number().int().nonnegative(),
                })
                .strict(),
              allSponsoredProducts: z
                .object({
                  spend: z.number().finite().nonnegative(),
                  sales: z.number().finite().nonnegative(),
                  orders: z.number().int().nonnegative(),
                })
                .strict(),
            })
            .strict(),
        ),
        allSponsoredProducts: z
          .object({
            spend: z.number().finite().nonnegative(),
            sales: z.number().finite().nonnegative(),
            orders: z.number().int().nonnegative(),
            observedAcos: z.number().finite().nonnegative().nullable(),
          })
          .strict(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type SmartCreationRecommendationEvidence = z.infer<
  typeof SmartCreationRecommendationEvidenceSchema
>;

const SmartCreationRecommendationRangeSchema = z
  .object({
    min: z.number().finite().nonnegative(),
    max: z.number().finite().positive(),
  })
  .strict()
  .refine((range) => range.min <= range.max, { message: "min must not exceed max" });

export const SmartCreationObjectiveRecommendationSchema = z
  .object({
    id: z.string().uuid(),
    shopId: z.string().uuid(),
    algorithmVersion: z.literal(1),
    basedOnRevision: z.number().int().positive(),
    draftFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    applicability: SmartCreationRecommendationApplicabilitySchema,
    objective: SmartCreationObjectiveSchema,
    riskPreference: RiskPreferenceV1Schema,
    aiPersonality: z.number().int().min(1).max(5).optional(),
    promotionDateRange: SmartCreationPromotionDateRangeSchema.nullable(),
    generatedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    confidence: z.enum(["low", "medium", "high"]),
    mode: z.enum(["historical", "fallback"]),
    fallbackReason: SmartCreationRecommendationFallbackReasonSchema.nullable(),
    evidence: SmartCreationRecommendationEvidenceSchema,
    targetAcos: z
      .object({
        value: z.number().finite().min(0.01).max(2),
        range: SmartCreationRecommendationRangeSchema,
        source: z.enum(["selected_product_history", "system_policy", "default_policy"]),
      })
      .strict(),
    totalDailyBudget: z
      .object({
        value: z.number().finite().positive().max(1_000_000),
        range: SmartCreationRecommendationRangeSchema,
        source: z.enum(["selected_product_history", "capability_floor"]),
        currencyCode: z.string().regex(/^[A-Z]{3}$/),
        decimals: z.number().int().min(0).max(4),
      })
      .strict(),
  })
  .strict();
export type SmartCreationObjectiveRecommendation = z.infer<
  typeof SmartCreationObjectiveRecommendationSchema
>;

const SmartCreationTargetAcosRecommendationSnapshotSchema = z
  .object({
    field: z.literal("targetAcos"),
    acceptedValue: z.number().finite().min(0.01).max(2),
    acceptedAt: z.string().datetime(),
    recommendation: SmartCreationObjectiveRecommendationSchema,
  })
  .strict()
  .refine((snapshot) => snapshot.acceptedValue === snapshot.recommendation.targetAcos.value, {
    message: "acceptedValue must match the Target ACOS recommendation",
  });

const SmartCreationTotalDailyBudgetRecommendationSnapshotSchema = z
  .object({
    field: z.literal("totalDailyBudget"),
    acceptedValue: z.number().finite().positive().max(1_000_000),
    acceptedAt: z.string().datetime(),
    recommendation: SmartCreationObjectiveRecommendationSchema,
  })
  .strict()
  .refine((snapshot) => snapshot.acceptedValue === snapshot.recommendation.totalDailyBudget.value, {
    message: "acceptedValue must match the Total Daily Budget recommendation",
  });

export const SmartCreationRecommendationSnapshotSchema = z.union([
  SmartCreationTargetAcosRecommendationSnapshotSchema,
  SmartCreationTotalDailyBudgetRecommendationSnapshotSchema,
]);
export type SmartCreationRecommendationSnapshot = z.infer<
  typeof SmartCreationRecommendationSnapshotSchema
>;

function smartCreationObjectiveValueDecisionSchema<T extends z.ZodTypeAny, S extends z.ZodTypeAny>(
  valueSchema: T,
  recommendationSnapshotSchema: S,
) {
  return z
    .object({
      finalValue: valueSchema,
      mode: z.enum(["manual", "accepted", "overridden"]),
      recommendationSnapshot: recommendationSnapshotSchema.nullable(),
    })
    .strict();
}

export const SmartCreationObjectiveDecisionsSchema = z
  .object({
    targetAcos: smartCreationObjectiveValueDecisionSchema(
      z.number().finite().min(0.01).max(2),
      SmartCreationTargetAcosRecommendationSnapshotSchema,
    ).nullable(),
    totalDailyBudget: smartCreationObjectiveValueDecisionSchema(
      z.number().finite().positive().max(1_000_000),
      SmartCreationTotalDailyBudgetRecommendationSnapshotSchema,
    ).nullable(),
  })
  .strict();
export type SmartCreationObjectiveDecisions = z.infer<typeof SmartCreationObjectiveDecisionsSchema>;
